import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import React from 'react';
import styled from 'styled-components/native';
import CameraIcon from '@/assets/icon/ic_camera2.svg';
import SccRemoteImage from '@/components/SccRemoteImage';
import { SccPressable } from '@/components/SccPressable';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import useNavigation from '@/navigation/useNavigation';
import ImageFileUtils from '@/utils/ImageFileUtils';
/**
 * 기존 사진(URL) + 새 사진(ImageFile) 통합 편집 UI.
 * - 기존 사진: SccRemoteImage로 표시 + 교체/삭제 버튼
 * - 교체된 사진: 새 사진 썸네일 + "교체됨" 배지 + 교체/삭제 버튼
 * - 새 사진: 썸네일 + 삭제 버튼
 * - 빈 슬롯: 카메라 버튼으로 사진 추가
 * - 최대 maxPhotos장
 */
export default function PhotoEditSlots({ title, description, target, existingPhotoUrls, replacedPhotos, newPhotos, deletedExistingIndices, maxPhotos, onDeleteExisting, onReplaceExisting, onChangeNewPhotos, }) {
    const navigation = useNavigation();
    const activeExistingPhotos = existingPhotoUrls
        .map((url, index) => ({ url, index }))
        .filter(item => !deletedExistingIndices.includes(item.index));
    const totalPhotos = activeExistingPhotos.length + newPhotos.length;
    const canAddMore = totalPhotos < maxPhotos;
    const takePhoto = () => {
        navigation.navigate('Camera', {
            takenPhotos: newPhotos,
            maxPhotos: Math.max(1, maxPhotos - activeExistingPhotos.length),
            onPhotosTaken: onChangeNewPhotos,
            target,
        });
    };
    const replacePhoto = (originalIndex) => {
        navigation.navigate('Camera', {
            takenPhotos: [],
            maxPhotos: 1,
            onPhotosTaken: (photos) => {
                if (photos.length > 0) {
                    onReplaceExisting(originalIndex, photos[0]);
                }
            },
            target,
        });
    };
    const deleteNewPhoto = (photo) => {
        onChangeNewPhotos(newPhotos.filter(p => p.uri !== photo.uri));
    };
    const viewExistingPhoto = (urls, index) => {
        navigation.navigate('ImageZoomViewer', {
            imageUrls: urls,
            index,
        });
    };
    const viewNewPhoto = (index) => {
        const imageUrls = newPhotos.map(p => ImageFileUtils.filepathFromImageFile(p));
        navigation.navigate('ImageZoomViewer', {
            imageUrls,
            index,
        });
    };
    // 표시할 슬롯 수 계산: 카메라 + 기존 + 새 사진, 총 3 슬롯 채우기
    const slotsUsed = (canAddMore ? 1 : 0) + activeExistingPhotos.length + newPhotos.length;
    const emptySlotsNeeded = Math.max(0, 3 - slotsUsed);
    return (_jsxs(Container, { children: [title !== undefined && (_jsxs(HeaderGroup, { children: [_jsx(Title, { children: title }), description !== undefined && (_jsx(DescriptionText, { children: description }))] })), _jsxs(SlotsRow, { children: [activeExistingPhotos.map((item, displayIndex) => {
                        const replacedPhoto = replacedPhotos.get(item.index);
                        return (_jsxs(Slot, { children: [_jsxs(ThumbnailButton, { elementName: "photo_edit_existing_thumbnail", onPress: () => {
                                        if (replacedPhoto) {
                                            navigation.navigate('ImageZoomViewer', {
                                                imageUrls: [
                                                    ImageFileUtils.filepathFromImageFile(replacedPhoto),
                                                ],
                                                index: 0,
                                            });
                                        }
                                        else {
                                            viewExistingPhoto(activeExistingPhotos.map(p => p.url), displayIndex);
                                        }
                                    }, children: [_jsx(ThumbnailWrapper, { children: replacedPhoto ? (_jsx(ThumbnailImage, { source: {
                                                    uri: ImageFileUtils.filepathFromImageFile(replacedPhoto),
                                                } })) : (_jsx(SccRemoteImage, { imageUrl: item.url, style: { flex: 1 }, resizeMode: "cover" })) }), replacedPhoto && (_jsx(ReplacedBadge, { children: _jsx(ReplacedBadgeText, { children: "\uAD50\uCCB4\uB428" }) }))] }), _jsxs(ActionButtonsRow, { children: [_jsx(ActionButton, { elementName: "photo_edit_replace_existing", onPress: () => replacePhoto(item.index), children: _jsx(ActionButtonText, { children: "\uAD50\uCCB4" }) }), _jsx(ActionButton, { elementName: "photo_edit_delete_existing", onPress: () => onDeleteExisting(item.index), children: _jsx(ActionButtonText, { children: "\uC0AD\uC81C" }) })] })] }, `existing-${item.index}`));
                    }), newPhotos.map((photo, index) => (_jsxs(Slot, { children: [_jsx(ThumbnailButton, { elementName: "photo_edit_new_thumbnail", onPress: () => viewNewPhoto(index), children: _jsx(ThumbnailWrapper, { children: _jsx(ThumbnailImage, { source: { uri: ImageFileUtils.filepathFromImageFile(photo) } }) }) }), _jsx(ActionButtonsRow, { children: _jsx(ActionButton, { elementName: "photo_edit_delete_new", onPress: () => deleteNewPhoto(photo), children: _jsx(ActionButtonText, { children: "\uC0AD\uC81C" }) }) })] }, `new-${photo.uri}`))), canAddMore && (_jsx(Slot, { children: _jsxs(CameraButton, { elementName: "photo_edit_camera_button", onPress: takePhoto, children: [_jsx(CameraIcon, { width: 36, height: 36 }), _jsx(CameraButtonText, { children: "\uC0AC\uC9C4 \uCD94\uAC00" })] }) })), Array.from({ length: emptySlotsNeeded }).map((_, i) => (_jsx(Slot, {}, `empty-${i}`)))] })] }));
}
// Styled components
const Container = styled.View `
  gap: 16px;
`;
const HeaderGroup = styled.View `
  gap: 2px;
`;
const Title = styled.Text `
  font-size: 18px;
  line-height: 26px;
  letter-spacing: -0.36px;
  font-family: ${font.pretendardSemibold};
  color: ${color.gray80v2};
`;
const DescriptionText = styled.Text `
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.28px;
  font-family: ${font.pretendardRegular};
  color: ${color.gray45};
`;
const SlotsRow = styled.View `
  flex-direction: row;
  justify-content: space-between;
  gap: 10px;
`;
const Slot = styled.View `
  flex: 1;
`;
const CameraButton = styled(SccPressable) `
  aspect-ratio: 1;
  border-color: ${color.gray15};
  background-color: ${color.gray15};
  border-width: 1px;
  border-radius: 14px;
  justify-content: center;
  align-items: center;
`;
const CameraButtonText = styled.Text `
  color: ${color.gray40};
  font-size: 12px;
  font-family: ${font.pretendardRegular};
  margin-top: 4px;
`;
const ThumbnailButton = styled(SccPressable) `
  aspect-ratio: 1;
`;
const ThumbnailWrapper = styled.View `
  flex: 1;
  overflow: hidden;
  border-radius: 14px;
`;
const ThumbnailImage = styled.Image `
  background-color: ${color.gray20};
  flex: 1;
`;
const ReplacedBadge = styled.View `
  position: absolute;
  bottom: 4px;
  left: 4px;
  background-color: ${color.brandColor};
  border-radius: 4px;
  padding: 2px 6px;
`;
const ReplacedBadgeText = styled.Text `
  font-size: 10px;
  font-family: ${font.pretendardMedium};
  color: white;
`;
const ActionButtonsRow = styled.View `
  flex-direction: row;
  justify-content: center;
  gap: 8px;
  margin-top: 6px;
`;
const ActionButton = styled(SccPressable) `
  padding: 4px 8px;
`;
const ActionButtonText = styled.Text `
  font-size: 12px;
  font-family: ${font.pretendardMedium};
  color: ${color.gray50};
`;
